import React from "react";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxButtons?: number; // default 7
}

const getPages = (
  current: number,
  total: number,
  maxButtons: number,
): (number | "...")[] => {
  const pages: (number | "...")[] = [];

  if (total <= maxButtons) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1);

  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);

  if (left > 2) pages.push("...");

  for (let i = left; i <= right; i++) {
    pages.push(i);
  }

  if (right < total - 1) pages.push("...");

  pages.push(total);

  return pages;
};

const CustomPagination: React.FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxButtons = 7,
}) => {
  if (totalPages <= 1) return null;

  const pages = getPages(currentPage, totalPages, maxButtons);

  return (
    <Pagination className="justify-content-center">
      {/* First */}
      <PaginationItem disabled={currentPage === 1}>
        <PaginationLink
          first
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onPageChange(1);
          }}
        />
      </PaginationItem>

      {/* Previous */}
      <PaginationItem disabled={currentPage === 1}>
        <PaginationLink
          previous
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onPageChange(currentPage - 1);
          }}
        />
      </PaginationItem>

      {/* Numbers */}
      {pages.map((page, index) =>
        page === "..." ? (
          <PaginationItem key={`dots-${index}`} disabled>
            <PaginationLink href="#">…</PaginationLink>
          </PaginationItem>
        ) : (
          <PaginationItem key={page} active={page === currentPage}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onPageChange(page as number);
              }}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        ),
      )}

      {/* Next */}
      <PaginationItem disabled={currentPage === totalPages}>
        <PaginationLink
          next
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onPageChange(currentPage + 1);
          }}
        />
      </PaginationItem>

      {/* Last */}
      <PaginationItem disabled={currentPage === totalPages}>
        <PaginationLink
          last
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onPageChange(totalPages);
          }}
        />
      </PaginationItem>
    </Pagination>
  );
};

export default CustomPagination;
